import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { DemoCaseResponse } from "@rnavoyager/contracts";
import { readDemoCase } from "./demo-api";
import { readDemoHistorySource } from "./demo-state";
import { demoDirectory, demoFileAccess } from "./demo-files";
import { WorkspaceFileAccessContext } from "./WorkspaceFileAccess";
import { SessionFileReferencesContext, SessionMarkdown } from "./SessionFileReferences";
import { ReportActionsContext } from "./ReportActions";
import { WorkspaceFilePreviewDialog } from "./WorkspaceFilePreview";
import { ChatMessage, TaskPlanCardView, TaskResultCard } from "./ChatWorkspaceParts";

/** 恢复展示记录不提交 Turn，也不把原案例事件混入当前会话的执行账本。 */
export function useDemoHistory(identityId: string, sessionId: string, initial?: DemoCaseResponse) {
  const [snapshot, setSnapshot] = useState<DemoCaseResponse | null>(initial ?? null);
  const [loading, setLoading] = useState(
    () => !initial && !!readDemoHistorySource(identityId, sessionId),
  );
  const [error, setError] = useState("");
  useEffect(() => {
    setSnapshot(initial ?? null);
    setError("");
    if (initial) {
      setLoading(false);
      return;
    }
    const source = readDemoHistorySource(identityId, sessionId);
    if (!source) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    void readDemoCase(source.caseId, controller.signal)
      .then((value) => {
        if (controller.signal.aborted) return;
        if (value.caseDigest !== source.caseDigest)
          throw new Error("研究案例已更新，原研究记录暂时无法加载");
        setSnapshot(value);
      })
      .catch((cause: unknown) => {
        if (!controller.signal.aborted)
          setError(cause instanceof Error ? cause.message : "研究记录暂时无法加载");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [identityId, sessionId, initial]);
  return { snapshot, loading, error };
}

/** 案例文件与报告只读展示，沿用公开案例的摘要绑定访问。 */
export function DemoHistoryScope({
  snapshot,
  children,
}: {
  snapshot: DemoCaseResponse;
  children: ReactNode;
}) {
  const access = useMemo(() => demoFileAccess(snapshot), [snapshot]);
  const [path, setPath] = useState<string | null>(null);
  const close = useCallback(() => setPath(null), []);
  const entry = path
    ? demoDirectory(
        snapshot.case,
        4,
        `/project/${path.slice(0, path.lastIndexOf("/"))}`,
      ).entries.find((item) => item.path === `/project/${path}`)
    : undefined;
  return (
    <WorkspaceFileAccessContext.Provider value={access}>
      <SessionFileReferencesContext.Provider
        value={{
          artifacts: snapshot.case.stages.flatMap((stage) =>
            stage.document.blocks.flatMap((block) =>
              block.kind === "artifacts" ? block.items : [],
            ),
          ),
          openFile: (value) => setPath(value.replace(/^\/project\//, "")),
        }}
      >
        <ReportActionsContext.Provider value={null}>
          {children}
          {entry && (
            <WorkspaceFilePreviewDialog entry={entry} sessionId="public-demo" onClose={close} />
          )}
        </ReportActionsContext.Provider>
      </SessionFileReferencesContext.Provider>
    </WorkspaceFileAccessContext.Provider>
  );
}

export function DemoHistory({
  snapshot,
  onOpenResult,
  inlineResultMedia,
}: {
  snapshot: DemoCaseResponse;
  onOpenResult: (id: string) => void;
  inlineResultMedia: boolean;
}) {
  const demo = snapshot.case;
  return (
    <section aria-label="已有研究记录">
      <DemoHistoryScope snapshot={snapshot}>
        <div className="timeline">
          <ChatMessage role="user" label="你">
            <SessionMarkdown sessionId="public-demo">{demo.goal}</SessionMarkdown>
          </ChatMessage>
          <TaskPlanCardView
            objective={demo.goal}
            preview="把序列识别与茎部构象分开比较，沿着证据检验假设。"
            steps={demo.stages.map((stage, index) => ({
              id: stage.stageId,
              kind: index === 4 ? "report" : index === 0 ? "inspect" : "tool",
              description: stage.title,
              acceptanceCriteria: stage.explanation,
            }))}
            assumptions={demo.hypotheses}
            awaitingStart={false}
            status={{ label: "已完成", tone: "success" }}
            busy={false}
          />
          {demo.stages.map((stage, index) => (
            <TaskResultCard
              key={stage.stageId}
              document={stage.document}
              summary={stage.explanation}
              inlineFeatured={index === 2 && inlineResultMedia}
              onOpenResult={onOpenResult}
              sessionId="public-demo"
              publicDemo
            />
          ))}
          <ChatMessage role="system" label="RNAVoyager">
            <SessionMarkdown sessionId="public-demo">{demo.summary}</SessionMarkdown>
          </ChatMessage>
        </div>
      </DemoHistoryScope>
    </section>
  );
}
